import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  Building2,
  IdCard,
  FileText,
  Receipt,
  CalendarClock,
  Globe,
  UserPlus,
  Ruler,
  Database,
  Layers,
  TrendingUp,
} from "lucide-react";
import useEscapeKey from "../../_hooks/useEscapeKey";
import { getNavigationCommands } from "./navigationCommands";
import { contentHeaderIconConfig } from "../../_config/iconConfig";
import { useFilterQuery, getEntityFilterConfig } from "../../_filtering";
import { useGetAllCompanyQuery } from "../../_features/company/api/companyApi";
import { useGetContactsByOrgIdQuery } from "../../_features/contacts/api/contactsApi";
import { useListProposalsQuery } from "../../_features/proposals/api/proposalApi";
import { useListInvoicesQuery } from "../../_features/invoices/api/invoiceApi";
import { openModal } from "../../_app/entityModals/store";
import "./_command-center.scss";

const NAV_COMMANDS = getNavigationCommands();

// The first 4 go through CommandCenterProvider's own useQuickCreateModals
// (pre-dates the entity-modal opener, see that hook's docstring) - the rest
// are every other entity ENTITY_MODAL_REGISTRY now covers (see
// _app/entityModals/registry.js), opened directly via the shared opener
// instead of extending that older mechanism for entities it never knew
// about. Both call sites land in the same "Quick actions" list either way.
const QUICK_ACTIONS = [
  { id: "action-company", label: "Add company", icon: Building2, run: (ctx) => ctx.openCompanyModal() },
  { id: "action-proposal", label: "New proposal", icon: FileText, run: (ctx) => ctx.openProposalModal() },
  { id: "action-subscription", label: "New subscription", icon: CalendarClock, run: (ctx) => ctx.openSubscriptionModal() },
  { id: "action-invoice", label: "New invoice", icon: Receipt, run: (ctx) => ctx.openInvoiceModal() },
  { id: "action-market", label: "Add market", icon: Globe, run: () => openModal("create_market") },
  { id: "action-user", label: "Add user", icon: UserPlus, run: () => openModal("create_user") },
  { id: "action-billing-unit", label: "Add billing unit", icon: Ruler, run: () => openModal("create_billing_unit") },
  { id: "action-usage-source", label: "Add usage source", icon: Database, run: () => openModal("create_usage_source") },
  { id: "action-usage-compilation", label: "Add usage compilation", icon: Layers, run: () => openModal("create_usage_compilation") },
  { id: "action-upsell", label: "Add upsell/downsell", icon: TrendingUp, run: () => openModal("create_upsell") },
];

// Which quick-create action is "the" one for the page you're currently on -
// that action floats to the top of Quick actions instead of the fixed order
// above, so the palette leads with what's relevant here first. The other
// three stay right below rather than disappearing - most pages (Contacts,
// Plans, Settings, ...) have no page-specific create modal wired into this
// palette at all, so hiding the rest would leave quick actions empty there.
const PAGE_ACTION_MATCHERS = [
  { prefix: "/company", actionId: "action-company" },
  { prefix: "/sell/proposals", actionId: "action-proposal" },
  { prefix: "/subscriptions", actionId: "action-subscription" },
  { prefix: "/collect/invoice", actionId: "action-invoice" },
];

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 250;
const MAX_ENTITY_RESULTS = 5;
const MAX_UNFILTERED_PAGES = 8;

const useDebouncedValue = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/**
 * Global ⌘K/Ctrl+K command center. Reuses each entity's existing search-by-
 * name list query (same `useFilterQuery`/`getEntityFilterConfig` machinery
 * every list page's header search already runs through) rather than a new
 * cross-entity backend endpoint - none exists, and this needed none to be
 * genuinely useful. Companies and proposals have real detail routes and
 * deep-link straight to the record; contacts and invoices don't (they open
 * via modal/PDF viewer from their list page, not a route), so those two
 * jump to the list page itself instead of fabricating a fake URL.
 */
const CommandCenter = ({ isOpen, onClose, openCompanyModal, openProposalModal, openSubscriptionModal, openInvoiceModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const mouseDownOnOverlay = useRef(false);

  const [term, setTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const debouncedTerm = useDebouncedValue(term.trim(), DEBOUNCE_MS);
  const canSearchEntities = isOpen && debouncedTerm.length >= MIN_SEARCH_LENGTH;

  useEscapeKey(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    setTerm("");
    setActiveIndex(0);
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedTerm]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const companyFilter = useFilterQuery({
    selectedFilters: { search: debouncedTerm },
    extras: getEntityFilterConfig("company").listExtras,
    initialPageSize: MAX_ENTITY_RESULTS,
  });
  const { data: companyData, isFetching: isFetchingCompanies } = useGetAllCompanyQuery(companyFilter.listArgs, {
    skip: !canSearchEntities,
  });

  const contactFilter = useFilterQuery({
    selectedFilters: { search: debouncedTerm },
    extras: getEntityFilterConfig("contact").listExtras,
    serializeOptions: getEntityFilterConfig("contact").serializeOptions,
    initialPageSize: MAX_ENTITY_RESULTS,
  });
  const { data: contactData, isFetching: isFetchingContacts } = useGetContactsByOrgIdQuery(contactFilter.listArgs, {
    skip: !canSearchEntities,
  });

  const proposalFilter = useFilterQuery({
    selectedFilters: { search: debouncedTerm },
    extras: getEntityFilterConfig("proposal").listExtras,
    initialPageSize: MAX_ENTITY_RESULTS,
  });
  const { data: proposalData, isFetching: isFetchingProposals } = useListProposalsQuery(proposalFilter.listArgs, {
    skip: !canSearchEntities,
  });

  const invoiceFilter = useFilterQuery({
    selectedFilters: { search: debouncedTerm },
    extras: getEntityFilterConfig("invoice").listExtras,
    serializeOptions: getEntityFilterConfig("invoice").serializeOptions,
    initialPageSize: MAX_ENTITY_RESULTS,
  });
  const { data: invoiceData, isFetching: isFetchingInvoices } = useListInvoicesQuery(invoiceFilter.listArgs, {
    skip: !canSearchEntities,
  });

  const isSearchingEntities = canSearchEntities && (isFetchingCompanies || isFetchingContacts || isFetchingProposals || isFetchingInvoices);

  const normalizedTerm = term.trim().toLowerCase();

  const pageActionId = useMemo(
    () => PAGE_ACTION_MATCHERS.find((entry) => location.pathname.startsWith(entry.prefix))?.actionId,
    [location.pathname]
  );

  const orderedActions = useMemo(() => {
    if (!pageActionId) return QUICK_ACTIONS;
    const primary = QUICK_ACTIONS.find((action) => action.id === pageActionId);
    if (!primary) return QUICK_ACTIONS;
    return [primary, ...QUICK_ACTIONS.filter((action) => action.id !== pageActionId)];
  }, [pageActionId]);

  const filteredActions = useMemo(
    () => (normalizedTerm ? orderedActions.filter((action) => action.label.toLowerCase().includes(normalizedTerm)) : orderedActions),
    [normalizedTerm, orderedActions]
  );

  const filteredPages = useMemo(() => {
    const matches = normalizedTerm ? NAV_COMMANDS.filter((cmd) => cmd.label.toLowerCase().includes(normalizedTerm)) : NAV_COMMANDS;
    return normalizedTerm ? matches : matches.slice(0, MAX_UNFILTERED_PAGES);
  }, [normalizedTerm]);

  // Split by each command's own `group` ("Pages" vs "Settings") instead of
  // one merged bucket - a flattened "Pages" section hid which rail an item
  // actually lives in, so it wasn't obvious where you'd land.
  const pageSections = useMemo(() => {
    const byGroup = new Map();
    filteredPages.forEach((cmd) => {
      if (!byGroup.has(cmd.group)) byGroup.set(cmd.group, []);
      byGroup.get(cmd.group).push(cmd);
    });
    return Array.from(byGroup.entries()).map(([title, items]) => ({ title, items }));
  }, [filteredPages]);

  const companyResults = useMemo(
    () =>
      (companyData?.companies || []).map((company) => ({
        id: `company-${company._id}`,
        label: company.isIndividual ? company.individualName : company.companyName,
        sublabel: company.email || "",
        icon: Building2,
        run: (ctx) => ctx.navigate(`/company/${company._id}`),
      })),
    [companyData]
  );

  const contactResults = useMemo(
    () =>
      (contactData?.contacts || []).map((contact) => ({
        id: `contact-${contact._id}`,
        label: contact.contactName,
        sublabel: contact.email || contact.companyName || "",
        icon: IdCard,
        run: (ctx) => ctx.navigate("/contacts"),
      })),
    [contactData]
  );

  const proposalResults = useMemo(
    () =>
      (proposalData?.proposals || []).map((proposal) => ({
        id: `proposal-${proposal._id}`,
        label: proposal.title,
        sublabel: proposal.company?.companyName || "",
        icon: FileText,
        run: (ctx) => ctx.navigate(`/sell/proposals/${proposal._id}/edit`),
      })),
    [proposalData]
  );

  const invoiceResults = useMemo(
    () =>
      (invoiceData?.invoices || []).map((invoice) => ({
        id: `invoice-${invoice._id}`,
        label: invoice.invoiceNumber,
        sublabel: invoice.company?.companyName || "",
        icon: Receipt,
        run: (ctx) => ctx.navigate("/collect/invoice"),
      })),
    [invoiceData]
  );

  // Kept separate from entitySections so the empty/loading states below can
  // tell "nothing typed matches a quick action or page" apart from "the
  // entity search hasn't come back yet" - they need different messaging.
  const nonEntitySections = useMemo(
    () => [{ title: "Quick actions", items: filteredActions }, ...pageSections].filter((section) => section.items.length > 0),
    [filteredActions, pageSections]
  );

  const entitySections = useMemo(() => {
    if (!canSearchEntities) return [];
    return [
      { title: "Companies", items: companyResults },
      { title: "Contacts", items: contactResults },
      { title: "Proposals", items: proposalResults },
      { title: "Invoices", items: invoiceResults },
    ].filter((section) => section.items.length > 0);
  }, [canSearchEntities, companyResults, contactResults, proposalResults, invoiceResults]);

  const sections = useMemo(() => [...nonEntitySections, ...entitySections], [nonEntitySections, entitySections]);

  const flatItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);

  useEffect(() => {
    const activeEl = listRef.current?.querySelector(".command-center__item--active");
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const runContext = { navigate, openCompanyModal, openProposalModal, openSubscriptionModal, openInvoiceModal };

  const runItem = (item) => {
    item.run(runContext);
    onClose();
  };

  const renderItem = (item, index) => {
    const Icon = item.icon || contentHeaderIconConfig[item.path] || ArrowRight;
    return (
      <button
        type="button"
        key={item.id}
        className={`command-center__item ${index === activeIndex ? "command-center__item--active" : ""}`}
        onMouseEnter={() => index >= 0 && setActiveIndex(index)}
        onClick={() => runItem(item)}
      >
        <span className="command-center__item-icon">
          <Icon size={15} strokeWidth={2} />
        </span>
        <span className="command-center__item-label">{item.label}</span>
        {item.sublabel && <span className="command-center__item-sublabel">{item.sublabel}</span>}
        <ArrowRight size={13} className="command-center__item-arrow" />
      </button>
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (flatItems.length ? (prev + 1) % flatItems.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => (flatItems.length ? (prev - 1 + flatItems.length) % flatItems.length : 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = flatItems[activeIndex];
      if (item) runItem(item);
    }
  };

  if (!isOpen) return null;

  // Entity results (companies/contacts/proposals/invoices) lag behind the
  // debounce + network round trip, so while they're still in flight we show
  // a "searching" row instead of ever letting the panel read as truly
  // empty - "No results found" flashing between keystrokes and results
  // arriving reads like a dead end even though results are on the way.
  const hasAnyResults = sections.length > 0;
  const showSearchingRow = isSearchingEntities && entitySections.length === 0;
  const showEmpty = !hasAnyResults && !isSearchingEntities;

  return createPortal(
    <div
      className="command-center__overlay"
      onMouseDown={(event) => {
        mouseDownOnOverlay.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && mouseDownOnOverlay.current) onClose();
        mouseDownOnOverlay.current = false;
      }}
    >
      <div className="command-center__panel" role="dialog" aria-modal="true" aria-label="Command center">
        <div className="command-center__search">
          <Search size={16} className="command-center__search-icon" />
          <input
            ref={inputRef}
            className="command-center__input"
            type="text"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search companies, contacts, proposals, invoices, or jump to a page..."
            autoComplete="off"
            spellCheck={false}
          />
          {isSearchingEntities && <span className="command-center__spinner" aria-label="Loading" />}
          <button type="button" className="command-center__kbd" onClick={onClose} aria-label="Close command center">
            esc
          </button>
        </div>

        <div className="command-center__results" ref={listRef}>
          {showEmpty ? (
            <div className="command-center__empty">
              {normalizedTerm ? (
                <>
                  <p className="command-center__empty-title">Nothing turned up for &ldquo;{term.trim()}&rdquo;</p>
                  <p className="command-center__empty-subtitle">Try a different spelling, or pick a quick action below</p>
                  <button type="button" className="command-center__empty-reset" onClick={() => setTerm("")}>
                    Clear search
                  </button>
                  <div className="command-center__empty-actions">
                    {orderedActions.map((item) => renderItem(item, -1))}
                  </div>
                </>
              ) : (
                <p className="command-center__empty-title">Start typing to search, or jump to a page</p>
              )}
            </div>
          ) : (
            <>
              {sections.map((section) => (
                <div className="command-center__section" key={section.title}>
                  <div className="command-center__section-title">{section.title}</div>
                  {section.items.map((item) => renderItem(item, flatItems.indexOf(item)))}
                </div>
              ))}
              {showSearchingRow && (
                <div className="command-center__searching">
                  <span className="command-center__spinner" aria-hidden="true" />
                  Digging through companies, contacts, proposals &amp; invoices for &ldquo;{term.trim()}&rdquo;&hellip;
                </div>
              )}
            </>
          )}
        </div>

        <div className="command-center__footer">
          <span className="command-center__hint">
            <span className="command-center__kbd">
              <ArrowUp size={10} />
            </span>
            <span className="command-center__kbd">
              <ArrowDown size={10} />
            </span>
            Navigate
          </span>
          <span className="command-center__hint">
            <span className="command-center__kbd">
              <CornerDownLeft size={10} />
            </span>
            Select
          </span>
          <span className="command-center__hint">
            <span className="command-center__kbd">esc</span>
            Close
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommandCenter;
